import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { FormContext, useField } from '../FormContext';
import { FormPreviewContext } from '../../engine/FormPreviewEngine';
import CurrencyConversionFieldView from '../view/CurrencyConversionFieldView';
import { defaultCurrency } from '../Currency';
import fetchAPI from '../../../utils/fetchAPI';
import {
  currencies,
  getCurrencyCodeByName
} from '../../../internal-components/form-builder/currencies';
import { FetchCallbacksContext } from '../../../internal-components/FetchCallbacks';
import getUTCNoonForDate from '../../../utils/getUTCNoonForDate';
import moment from '../../../utils/momentWithLocale';
import useTranslations from '../../../hooks/useTranslations';

const CurrencyConversionFieldEngine = ({
  internalId,
  lookupInfo = {},
  options,
  readOnly,
  required,
  setValue
}) => {
  const t = useTranslations();
  const { openSnackbar } = React.useContext(FetchCallbacksContext);
  const formContextProps = useContext(FormContext);
  const formPreviewContextProps = useContext(FormPreviewContext);

  const mappedFields =
    formContextProps.mappedFields || formPreviewContextProps.mappedFields;
  const initialUserSubmission = formContextProps.initialUserSubmission || null;
  const { formId, onFormChange, validateField } = formContextProps;

  const [subFields, setSubFields] = useState([]);
  const baseCurrency = options[0] || defaultCurrency;
  const [subFieldValues, setSubFieldValues] = useState([]);
  const [calculating, setCalculating] = useState(false);
  const lookupSubFields = lookupInfo.subFields;

  const initialSubValues = useRef([]);

  useEffect(() => {
    let fields = [],
      matchingField = null,
      results = [],
      fieldToPush = null;
    if (mappedFields) {
      fields = cloneDeep(mappedFields);
    }
    lookupSubFields.forEach(lookup => {
      if (lookup && lookup.internalId) {
        fieldToPush = null;
        matchingField = Object.values(fields).find(one => {
          return !!(
            one &&
            one.lookupInfo &&
            one.lookupInfo.subFieldInfo &&
            one.lookupInfo.subFieldInfo.parentFieldId === internalId &&
            one.lookupInfo.subFieldInfo.externalFormFieldId ===
              lookup.internalId
          );
        });
        if (matchingField) {
          fieldToPush = cloneDeep(matchingField);
          fieldToPush.internalId = lookup.internalId;
        }
        if (fieldToPush) {
          results.push({
            ...fieldToPush,
            localFormFieldId: lookup.localFormFieldId
          });
          if (
            matchingField.internalId &&
            initialUserSubmission &&
            initialUserSubmission.endUserInput
          ) {
            if (
              typeof initialUserSubmission.endUserInput[
                matchingField.internalId
              ] !== 'undefined'
            ) {
              initialSubValues.current.push(
                initialUserSubmission.endUserInput[matchingField.internalId]
              );
            } else if (
              initialUserSubmission.adminInput &&
              typeof initialUserSubmission.adminInput[
                matchingField.internalId
              ] !== 'undefined'
            ) {
              initialSubValues.current.push(
                initialUserSubmission.adminInput[matchingField.internalId]
              );
            } else {
              initialSubValues.current.push('');
            }
          }
        }
      }
    });
    if (!initialSubValues.current.length) {
      initialSubValues.current[0] = currencies[baseCurrency].name;
      initialSubValues.current[2] = getUTCNoonForDate(moment(new Date()));
    }
    setSubFields(results);
    setSubFieldValues(initialSubValues.current);
  }, [mappedFields, lookupSubFields]);

  const getConversion = () => {
    let newSubFieldValues = [...subFieldValues];
    const sourceCurrency = getCurrencyCodeByName(subFieldValues[0]) || '';
    const sourceValue = Number(subFieldValues[1]) || 0;
    const destinationCurrency = baseCurrency;
    const conversionDate = subFieldValues[2];
    if (subFieldValues[0] && subFieldValues[2]) {
      if (!subFieldValues[1] || subFieldValues[1] === '0') {
        newSubFieldValues[3] = 0;
        setSubFieldValues(newSubFieldValues);
        setCalculating(false);
        onFormChange();
      } else {
        fetchAPI(
          `/forms/v1/public/forms/${formId}/field/${internalId}/exchange?sourceValue=${sourceValue}&sourceCurrency=${sourceCurrency}&destinationCurrency=${destinationCurrency}&conversionDate=${conversionDate}`
        )
          .then(res => {
            newSubFieldValues[3] = res && res.value ? res.value : 0;
            setSubFieldValues(newSubFieldValues);
            setCalculating(false);
            onFormChange();
          })
          .catch(e => {
            newSubFieldValues[3] = 0;
            setSubFieldValues(newSubFieldValues);
            setCalculating(false);
            onFormChange();
            openSnackbar('error', 'snackbars.currency_conversion.error');
          });
      }
    } else if (subFieldValues[3]) {
      newSubFieldValues[3] = 0;
      setSubFieldValues(newSubFieldValues);
      setCalculating(false);
      onFormChange();
    }
  };

  const counter = useRef(0);
  const getThrottledConversion = () => {
    setCalculating(true);
    counter.current += 1;

    setTimeout(() => {
      counter.current -= 1;
      if (counter.current === 0) {
        getConversion();
      }
    }, 1000);
  };

  const clearResult = useCallback(() => {
    setSubFieldValue(3, 0);
  }, [subFieldValues]);

  const setResultToIdentity = useCallback(() => {
    setSubFieldValue(3, subFieldValues[1]);
  }, [subFieldValues]);

  useEffect(() => {
    if (!initialSubValues.current.length || subFieldValues.length < 3) {
      return;
    }
    if (
      !subFieldValues[0] ||
      !Number(subFieldValues[1]) ||
      !subFieldValues[2]
    ) {
      clearResult();
      return;
    }
    if (getCurrencyCodeByName(subFieldValues[0]) === baseCurrency) {
      setResultToIdentity();
      return;
    }
    if (
      initialSubValues.current[0] === subFieldValues[0] &&
      initialSubValues.current[1] === subFieldValues[1] &&
      initialSubValues.current[2] === subFieldValues[2]
    ) {
      return;
    }

    getThrottledConversion();
  }, [
    initialSubValues.current,
    subFieldValues[0],
    subFieldValues[1],
    subFieldValues[2]
  ]);

  useEffect(() => {
    setValue(subFieldValues[3]);
  }, [subFieldValues[3]]);

  const onSubFieldFocus = useCallback(
    index => {
      const fieldNode = document.querySelector(
        `[field-id="${subFields[index].localFormFieldId}"]`
      );
      if (!fieldNode) {
        return;
      }
      const errorP = fieldNode.querySelector('[data-error]');
      if (!errorP) {
        return;
      }
      fieldNode.classList.remove('EUF-error');
      errorP.textContent = '';
    },
    [subFields, subFieldValues]
  );

  const onSubFieldBlur = useCallback(
    index => {
      validateField(subFields[index].localFormFieldId, subFieldValues[index]);
    },
    [subFields, subFieldValues]
  );

  const setSubFieldValue = useCallback(
    (index, value) => {
      let newSubFieldValues = [...subFieldValues];
      newSubFieldValues[index] = value;
      setSubFieldValues(newSubFieldValues);
      onFormChange();
    },
    [subFieldValues]
  );

  const validator = useCallback(() => {
    let error = '';

    const conversionValue = subFieldValues[1];
    const conversionValueAcceptable = conversionValue === 0 || conversionValue;

    if (required && !conversionValueAcceptable) {
      error = t('forms.fields.error.required');
    }

    return error;
  }, [required, subFieldValues, t]);

  useField(internalId, validator);

  const viewProps = {
    baseCurrency,
    calculating,
    onSubFieldFocus,
    onSubFieldBlur,
    readOnly,
    subFields,
    subFieldValues,
    setSubFieldValue
  };

  return <CurrencyConversionFieldView {...viewProps} />;
};

export default CurrencyConversionFieldEngine;
